import { useMemo, useState } from 'react';
import PageTransition from '@/components/ui/PageTransition';
import { Empty, Button } from 'antd';
import { MessageOutlined } from '@ant-design/icons';
import { AutoRefreshToggle } from '@/components/ui/AutoRefreshToggle';
import { PageHeader } from '@/components/ui/PageHeader';
import { ClientScopeEmpty } from '@/components/client-portal/ClientScopeEmpty';
import { createFileRoute } from '@tanstack/react-router';
import {
  useGetChatThreadsQuery,
  useGetMyUnifiedThreadsQuery,
  type ChatThread
} from '@/api/client-communications';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTranslation } from 'react-i18next';

import {
  ThreadsStats,
  ThreadsFilters,
  ThreadsTable,
  type FlowOption
} from '@/components/threads';
import { toFlowOption } from '@/components/threads/utils';
import { AUTOREFRESH_INTERVAL } from '@/constants/autorefresh';
import { useLastUpdatedText } from '@/hooks/useLastUpdatedText';
import { useClientPortalScope } from '@/hooks/useClientPortalScope';
import { useThreadListFilters } from '@/hooks/threads/useThreadListFilters';

dayjs.extend(relativeTime);

export const Route = createFileRoute('/client/threads/')({
  component: ClientThreadsPage
});

function ClientThreadsPage() {
  type NormalizedThreadsData = {
    clientId: string;
    flows: Array<{ flowId: string; flowName?: string }>;
    threads: ChatThread[];
  };

  const { t } = useTranslation('manage');
  const { t: tDashboard } = useTranslation('dashboard');
  const { isAdmin, isCustomer, adminScopeReady, selectedCustomerId, selectedClientId } = useClientPortalScope();

  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);

  const myThreadsQuery = useGetMyUnifiedThreadsQuery(
    isCustomer,
    autoRefresh ? AUTOREFRESH_INTERVAL : false
  );
  const adminThreadsQuery = useGetChatThreadsQuery(
    { userId: selectedCustomerId ?? undefined },
    isAdmin && adminScopeReady,
    autoRefresh ? AUTOREFRESH_INTERVAL : false
  );

  const activeQuery = isCustomer ? myThreadsQuery : adminThreadsQuery;
  const { data, isLoading, isError, refetch, isFetching, dataUpdatedAt } = activeQuery;

  const threadsData = useMemo<NormalizedThreadsData | null>(() => {
    if (isCustomer && data && 'threads' in data) {
      return {
        clientId: '',
        flows: [],
        threads: data.threads ?? []
      };
    }
    if (!data || !('threads' in data) || !('flows' in data)) return null;
    return {
      clientId: data.clientId,
      flows: data.flows ?? [],
      threads: data.threads ?? []
    };
  }, [data, isCustomer]);

  const lastUpdatedText = useLastUpdatedText(dataUpdatedAt);

  // Search, date and column filters (customers have no user type column)
  const { filters, tableFilters, setTableFilters, updateFilter, resetFilters, filteredThreads, hasActiveFilters } =
    useThreadListFilters(threadsData?.threads, threadsData?.flows);

  const flowOptions: FlowOption[] = useMemo(() => {
    const fromApi = threadsData?.flows ?? [];
    if (fromApi.length) {
      return fromApi.map((flow) => toFlowOption(flow.flowId, flow.flowName));
    }

    const byFlowId = new Map<string, string>();
    for (const thread of threadsData?.threads ?? []) {
      if (!thread.flowId) continue;
      byFlowId.set(thread.flowId, thread.flowName || thread.flowId);
    }
    return [...byFlowId.entries()].map(([flowId, flowName]) => toFlowOption(flowId, flowName));
  }, [threadsData?.flows, threadsData?.threads]);

  const totalThreads = threadsData?.threads?.length || 0;
  const totalFlows = flowOptions.length;
  const recentThreads =
    threadsData?.threads?.filter((thread) =>
      dayjs(thread.updatedAt).isAfter(dayjs().subtract(24, 'hours'))
    ).length || 0;

  if (isAdmin && !adminScopeReady) {
    return <ClientScopeEmpty description={tDashboard('client.scopeRequired.threads')} />;
  }

  if (isError) {
    return (
      <PageTransition animation="fadeInUp">
        <Empty
          description={t('threads.loadError')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" onClick={() => void refetch()}>
            {t('threads.tryAgain')}
          </Button>
        </Empty>
      </PageTransition>
    );
  }

  return (
    <PageTransition animation="fadeInUp">
      <PageHeader
        icon={<MessageOutlined />}
        title={t('threads.title')}
        description={t('threads.description')}
        actions={
          <AutoRefreshToggle
            autoRefresh={autoRefresh}
            onAutoRefreshChange={setAutoRefresh}
            isFetching={isFetching}
            lastUpdated={lastUpdatedText}
          />
        }
      />

      <ThreadsStats
        stats={{
          totalThreads,
          filteredCount: filteredThreads.length,
          totalFlows,
          recentThreads,
          hasActiveFilters,
          isLoading
        }}
        showFlowStat={!isCustomer}
      />

      <ThreadsFilters
        filters={filters}
        filteredCount={filteredThreads.length}
        totalCount={totalThreads}
        hasActiveFilters={hasActiveFilters}
        isFetching={isFetching}
        onFilterChange={updateFilter}
        onRefresh={refetch}
        onReset={resetFilters}
      />

      <ThreadsTable
        threads={filteredThreads}
        totalThreads={totalThreads}
        flowOptions={flowOptions}
        tableFilters={tableFilters}
        hasActiveFilters={hasActiveFilters}
        isLoading={isLoading}
        onTableFilterChange={(next) => setTableFilters({ ...next, userType: null })}
        onResetFilters={resetFilters}
        detailBasePath="/client/threads"
        showArchiveAction={false}
        simplifyForCustomer={isCustomer}
      />
    </PageTransition>
  );
}
